import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ElementRef,
  QueryList,
  ViewChildren,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router, RouterLink } from "@angular/router";
import { Capacitor } from "@capacitor/core";

@Component({
  selector: "app-landing",
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: "./landing.component.html",
  styleUrl: "./landing.component.css",
})
export class LandingComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren("animateOnScroll") animateElements!: QueryList<ElementRef>;

  isNavScrolled = false;
  mobileMenuOpen = false;
  private scrollHandler = () => this.onScroll();
  private observer!: IntersectionObserver;

  activeSport: "padel" | "tennis" | "table-tennis" = "padel";

  stats = [
    { value: "10+", label: "Partner Clubs" },
    { value: "200+", label: "Active Players" },
    { value: "500+", label: "Matches Played" },
  ];

  howItWorks = [
    {
      icon: "assets/images/location.png",
      title: "Find a Club",
      description:
        "Browse padel clubs near you with real-time court availability and pricing.",
    },
    {
      icon: "assets/images/court-bookings.png",
      title: "Book Instantly",
      description:
        "Pick your time slot, invite players, and confirm your booking in seconds.",
    },
    {
      icon: "assets/images/racket-paddle-app.png",
      title: "Play & Compete",
      description:
        "Track matches, earn RB Rank points, and climb the leaderboard.",
    },
  ];

  clubFeatures = [
    {
      icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
      title: "Online Booking System",
      description:
        "Real-time court availability with instant online reservations. No more phone calls.",
    },
    {
      icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
      title: "Analytics Dashboard",
      description:
        "Track revenue, occupancy rates, peak hours, and player engagement in real time.",
    },
    {
      icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
      title: "Employee Management",
      description:
        "Manual bookings, match approvals, player registrations -- all from one interface.",
    },
    {
      icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
      title: "Promotions & Memberships",
      description:
        "Create promotions, manage memberships, and handle fixed recurring bookings.",
    },
  ];

  constructor(private router: Router) {}

  ngOnInit() {
    if (Capacitor.isNativePlatform()) {
      this.router.navigate(["/clubs"], { replaceUrl: true });
      return;
    }
    window.addEventListener("scroll", this.scrollHandler, { passive: true });
  }

  ngAfterViewInit() {
    if (Capacitor.isNativePlatform()) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            this.observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" },
    );

    this.animateElements.forEach((el) => {
      this.observer.observe(el.nativeElement);
    });
  }

  ngOnDestroy() {
    window.removeEventListener("scroll", this.scrollHandler);
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private onScroll() {
    this.isNavScrolled = window.scrollY > 50;
  }

  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  closeMobileMenu() {
    this.mobileMenuOpen = false;
  }

  scrollTo(sectionId: string) {
    this.closeMobileMenu();
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  goToRegister() {
    this.closeMobileMenu();
    this.router.navigate(["/register"]);
  }

  goToLogin() {
    this.closeMobileMenu();
    this.router.navigate(["/login"]);
  }

  goToClubs() {
    this.closeMobileMenu();
    this.router.navigate(["/clubs"]);
  }
}
